import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@foundation/altzero_base/base/components/ui/button";
import { Input } from "@foundation/altzero_base/base/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@foundation/altzero_base/base/components/ui/dialog";
import { useToast } from "@foundation/altzero_base/base/hooks/use-toast";
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";
import { ArrowLeft, ArrowRight, Check, Clock, Download, FileText, Info, Loader2, MessageSquare, Save, ShieldAlert, Sparkles, X } from "lucide-react";
import { ProposalPagesEditor } from "../../../altzero_plugins/features/template-editor/Editor/features/Editor/components/PlateEditor/ProposalPagesEditor";
import {
  sectionsToPlatePages,
  buildCoverPage,
  buildTocPage,
  type BrandingForPlate as BrandingData,
} from "../../../altzero_plugins/features/template-editor/Editor/features/Editor/utils/markdownToPlate";
import { contentPagesToMarkdown } from "@/features/revisr/utils/platePagesToMarkdown";
import type { PlatePage, PlateEditorState } from "../../../altzero_plugins/features/template-editor/Editor/features/Editor/components/PlateEditor/types";
import {
  buildBrandColors,
  exportPlatePagesToPuppeteerPdf,
  exportPlatePagesToDocx,
  exportPlatePagesToHtml,
} from '@/features/revisr/utils/exportPlate';
import { toCssFontFamily } from '@/features/revisr/utils/fontFamilyCss';
import { PolicyFeedbackModal } from '@/features/revisr/components/feedback/PolicyFeedbackModal';
import { RegenerationDialog } from '@/features/revisr/components/editor/RegenerationDialog';
import { DocumentRegenerateDialog } from '@/features/revisr/components/editor/DocumentRegenerateDialog';
import { regenerateSection, regenerateDocument } from '@/features/revisr/services/policyGenerationService';
import type { RegenerateSectionRequest } from "../../../altzero_plugins/features/template-editor/Editor/features/Editor/components/PlateEditor/types";
import type { Section } from "../types/documentTypes";
import ScopingAILayout from "../components/ScopingAILayout";
import type { BrandWorkflowConfig, PolicyFooterState } from "../types/brandWorkflowConfig";

/** Parse a markdown string into sections. Splits on `## ` headings. */
function parseMarkdownToSections(
  markdown: string,
): Section[] {
  const result: Section[] = [];
  let idx = 0;
  const parts = markdown.split(/^## /m);
  for (const part of parts) {
    if (!part.trim()) continue;
    const newlineIdx = part.indexOf("\n");
    const title = newlineIdx === -1 ? part.trim() : part.slice(0, newlineIdx).trim();
    const content = newlineIdx === -1 ? "" : part.slice(newlineIdx + 1).trim();
    result.push({ id: `section-${idx++}`, title, content, blocks: [], status: "completed" });
  }
  if (result.length === 0 && markdown.trim()) {
    result.push({ id: "section-0", title: "Policy Content", content: markdown.trim(), blocks: [], status: "completed" });
  }
  return result;
}

interface ProposalResult {
  sections: Section[];
  branding: BrandingData | null;
  title: string;
}

/**
 * Shape accepted by applyResult — widened so it tolerates the looser
 * {@link BrandWorkflowConfig.loadFn} return (sections as {title,content}[],
 * branding as a plain Record) plus the editable-footer fields footer /
 * includeCoverToc that now travel alongside pages/zoom.
 */
type ApplyResultInput = {
  sections?: Section[] | { title: string; content: string }[];
  branding?: BrandingData | Record<string, unknown> | null;
  title?: string;
  pages?: unknown[];
  zoom?: number;
  fullContent?: string;
  footer?: PolicyFooterState;
  includeCoverToc?: boolean;
};

/** Recursively flatten a Plate node's children into their text content. */
const extractNodeText = (node: unknown): string => {
  if (!node || typeof node !== 'object') return '';
  const n = node as { text?: string; children?: unknown[] };
  if (typeof n.text === 'string') return n.text;
  if (Array.isArray(n.children)) return n.children.map(extractNodeText).join('');
  return '';
};

/**
 * Extract section titles from the CURRENT content pages by scanning each
 * page's plateValue for h2 nodes (the per-section heading sectionsToPlatePages
 * emits). The leading "N." number prefix is stripped so buildTocPage can
 * re-number cleanly. Cover (h1) and TOC (paragraphs) pages contribute no h2,
 * so passing the full live page list is safe.
 */
const extractContentPageTitles = (pages: unknown[]): string[] => {
  const titles: string[] = [];
  for (const page of pages) {
    const pv = (page as { plateValue?: unknown })?.plateValue;
    if (!Array.isArray(pv)) continue;
    for (const node of pv) {
      if ((node as { type?: string })?.type === 'h2') {
        const raw = extractNodeText(node).replace(/^\d+[\.\)]\s*/, '').trim();
        if (raw) titles.push(raw);
      }
    }
  }
  return titles;
};

/**
 * Detect whether a regen prompt is ONLY about the cover / table-of-contents
 * pages (so we can skip the LLM and just manipulate the page list), versus a
 * real content change that needs a full regenerate. Returns the parsed intent.
 */
const parseCoverTocIntent = (userContext: string) => {
  const ctx = userContext || '';
  const coverTocMention = /cover|table of contents|\btoc\b|contents page/i.test(ctx);
  const removeIntent = /\b(remove|delete|drop|take out|strip)\b/i.test(ctx);
  const addIntent = /\b(add|include|bring back|put back|re-?add|insert)\b/i.test(ctx);
  // "content change" = the prompt also asks to alter policy substance, not
  // just the cover/TOC pages. "add" alone is ambiguous (could be "add a
  // clause" vs "add the cover") so we require a substance word.
  const contentChange = /\b(section|clause|rewrite|reword|change|update|strengthen|modify|tighten|simplify|expand|policy|paragraph|bullet|requirement)\b/i.test(ctx)
    || /\badd (a |an |the )?(clause|section|paragraph|bullet|requirement|point)\b/i.test(ctx);
  return { coverTocMention, removeIntent, addIntent, contentChange };
};


const BrandedProposalEditor: React.FC<{ config?: BrandWorkflowConfig }> = ({ config }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user } = useUser();

  const LayoutComponent = config?.layout ?? ScopingAILayout;
  const sessionResultKey = config?.session?.resultKey ?? "scopingai_brand_proposal_result";
  const sessionDataKey = config?.session?.dataKey ?? "scopingai_brand_proposal_data";
  const brandFormRoute = config?.routes?.brandForm ?? "/scopingai/brand-proposal";

  // Append the policy ID to back navigation so the target page can restore content.
  const policyId = searchParams.get("id");
  const backRoute = policyId ? `${brandFormRoute}?id=${policyId}` : brandFormRoute;
  const afterSaveRoute = config?.routes?.afterSave ?? "/scopingai/proposals";

  // Seed the editor's previewMode from the ?tab= URL param
  const initialPreviewMode = searchParams.get("tab") === "preview";

  const previewRoute = config?.routes?.preview;

  const noun = config?.labels?.noun ?? "Proposal";
  const saveBtnLabel = config?.labels?.saveButton ?? "Save Proposal";
  const defaultTitle = config?.labels?.defaultDocTitle ?? "Branded Proposal";
  const accent = config?.accentColor ?? "emerald";

  const [sections, setSections] = useState<Section[]>([]);
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [proposalTitle, setProposalTitle] = useState(defaultTitle);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [editorState, setEditorState] = useState<unknown>(null);
  // Pages restored directly from a previously-saved editor state (completed policy).
  // When set, editorInitialData uses these instead of re-deriving from sections.
  const [storedPages, setStoredPages] = useState<unknown[] | null>(null);
  const [storedZoom, setStoredZoom] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [editorKey, setEditorKey] = useState(0);

  // Editable footer values (classification + policyName + orgName + date + note)
  // and the cover/TOC inclusion flag. Seeded from the generation session /
  // DB editor JSON; persisted back via saveFn + onNavigateToPreview.
  const [footer, setFooter] = useState<PolicyFooterState>({ classification: 'Public' });
  const [includeCoverToc, setIncludeCoverToc] = useState(true);

  // Save dialog state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [feedbackPolicyId, setFeedbackPolicyId] = useState<string | null>(null);

  // Export state
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Regenerate dialog state (new modulation-based flow)
  const [regenRequest, setRegenRequest] = useState<RegenerateSectionRequest | null>(null);
  const [showRegenDialog, setShowRegenDialog] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Whole-document regenerate dialog state
  const [showDocRegenDialog, setShowDocRegenDialog] = useState(false);
  const [isRegeneratingDoc, setIsRegeneratingDoc] = useState(false);
  const docRegenAbortRef = useRef<AbortController | null>(null);

  // On-demand feedback modal (separate from the save-triggered flow).
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Abort any in-flight whole-document regeneration when the component unmounts.
  useEffect(() => {
    return () => {
      docRegenAbortRef.current?.abort();
    };
  }, []);

  const brandColors = useMemo(() => buildBrandColors(branding), [branding]);

  // Resolved footer meta: editable footer values win, with sensible defaults
  // (proposal title / org name / today's date / Public) when the user hasn't
  // set them. Read by the canvas footer and all three export paths.
  const footerMeta = useMemo(() => ({
    classification: footer.classification || 'Public',
    policyName: footer.policyName ?? proposalTitle ?? undefined,
    orgName: footer.orgName ?? config?.orgName ?? branding?.brand_title ?? undefined,
    date: footer.date ?? new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    note: footer.note || undefined,
  }), [footer, proposalTitle, config, branding]);

  useEffect(() => {
    let cancelled = false;

    const applyResult = (
      result: ApplyResultInput,
      useStoredPages = true
    ) => {
      setSections((result.sections ?? []) as Section[]);
      setBranding((result.branding as BrandingData | null) ?? null);
      if (result.title) {
        setProposalTitle(result.title);
      } else if ((result.sections?.length ?? 0) > 0) {
        setProposalTitle(result.sections[0].title ?? defaultTitle);
      }
      // Editable footer values + cover/TOC flag travel alongside pages/zoom.
      if (result.footer) setFooter(result.footer);
      if (typeof result.includeCoverToc === 'boolean') setIncludeCoverToc(result.includeCoverToc);
      // Restore stored pages when:
      //   - they came from a DB load (useStoredPages=true), OR
      //   - they're plate-shaped (i.e. saved by this editor — these contain
      //     the user's final Slate value and must not be re-derived).
      // Legacy modern-shaped pages from old DB rows still set storedPages,
      // but the editor's initial-data memo ignores them (they have no
      // plateValue field) and falls through to deriving from sections.
      const looksPlateShaped =
        Array.isArray(result.pages) &&
        result.pages.length > 0 &&
        (result.pages[0] as { plateValue?: unknown })?.plateValue !== undefined;
      if (
        (useStoredPages || looksPlateShaped) &&
        Array.isArray(result.pages) &&
        result.pages.length > 0
      ) {
        setStoredPages(result.pages);
        setStoredZoom(result.zoom ?? 1);
      }
    };

    const applySessionInput = () => {
      try {
        const inputStored = sessionStorage.getItem(sessionDataKey);
        if (!inputStored) return;
        const inputPayload = JSON.parse(inputStored);
        if (inputPayload?.brandingData) {
          setBranding((prev) => ({
            ...inputPayload.brandingData,
            ...(prev ?? {}),
          }));
        }
        if (inputPayload?.orgId) {
          setOrgId(inputPayload.orgId as string);
        }
      } catch {
        // Non-fatal
      }
    };

    const loadData = async () => {
      // Priority 1: DB via loadFn (reliable, works across tabs/sessions)
      if (policyId && config?.loadFn) {
        try {
          const dbResult = await config.loadFn(policyId);
          if (!cancelled && dbResult) {
            applyResult(dbResult);
            applySessionInput();
            setIsLoaded(true);
            return;
          }
        } catch (err) {
          console.error("[BrandedProposalEditor] DB load failed, falling back to session:", err);
        }
      }

      // Priority 2: sessionStorage (same-tab navigation from generating page)
      const stored = sessionStorage.getItem(sessionResultKey);
      if (!stored) {
        if (!cancelled) navigate(backRoute);
        return;
      }

      try {
        const result = JSON.parse(stored) as ProposalResult & { pages?: unknown[]; zoom?: number };
        if (cancelled) return;
        // Pass false: always re-derive pages from sections via sectionsToPlatePages
        // (storedPages is only set if the session payload happens to contain
        // plate-shaped pages — see the looksPlateShaped check in applyResult).
        applyResult(result, false);
      } catch {
        if (!cancelled) navigate(backRoute);
        return;
      }

      applySessionInput();
      if (!cancelled) setIsLoaded(true);
    };

    loadData();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, policyId]);

  // Build the Plate editor's initial data. Two sources:
  //   1. Plate-shaped pages restored from session/DB (preserve user edits).
  //   2. Fresh build from AI sections + logo + title via sectionsToPlatePages.
  const editorInitialData = useMemo<Partial<PlateEditorState> | undefined>(() => {
    const restoredArePlate =
      Array.isArray(storedPages) &&
      storedPages.length > 0 &&
      (storedPages[0] as { plateValue?: unknown })?.plateValue !== undefined;
    if (restoredArePlate) {
      return {
        version: 2,
        pages: storedPages as PlateEditorState["pages"],
        currentPageId: (storedPages[0] as { id?: string })?.id ?? "",
        zoom: storedZoom,
        previewMode: initialPreviewMode,
      };
    }
    const hasContent =
      sections.length > 0 || !!branding?.logo_url || !!proposalTitle;
    if (!hasContent) return undefined;
    const pages = sectionsToPlatePages(sections, branding, proposalTitle);
    return {
      version: 2,
      pages,
      currentPageId: pages[0]?.id ?? "",
      zoom: 1,
      previewMode: initialPreviewMode,
    };
  }, [storedPages, storedZoom, sections, branding, proposalTitle, initialPreviewMode]);

  const editorConfig = {
    showSidebar: true,
    showSaveButton: false,
    theme: "light" as const,
    hideViewToggle: true,
    viewMode: (initialPreviewMode ? "preview" : "edit") as "preview" | "edit",
  };

  // When the same component instance is reused across routes (React reconciliation),
  // `initialPreviewMode` changes but the editor's internal state doesn't re-init.
  // Detect the change and force a full remount with the new mode.
  const prevPreviewModeRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevPreviewModeRef.current !== null && prevPreviewModeRef.current !== initialPreviewMode) {
      setEditorKey(k => k + 1);
    }
    prevPreviewModeRef.current = initialPreviewMode;
  }, [initialPreviewMode]);

  // Navigate to preview page — persists current editor state to DB and session, then navigates
  const handleNavigateToPreview = async () => {
    const currentPages = (editorState as any)?.pages ?? editorInitialData?.pages ?? [];
    const currentZoom = (editorState as any)?.zoom ?? storedZoom ?? 1;
    try {
      const stored = sessionStorage.getItem(sessionResultKey);
      const existing = stored ? JSON.parse(stored) : {};
      sessionStorage.setItem(sessionResultKey, JSON.stringify({
        ...existing,
        pages: currentPages.length > 0 ? currentPages : existing.pages,
        zoom: currentZoom,
        footer,
        includeCoverToc,
      }));
    } catch {
      // Non-fatal
    }
    // Persist changes to the backend before navigating so edits survive
    // tab switches, page reloads, and returning from the preview page.
    if (config?.onNavigateToPreview && policyId && user?.id) {
      try {
        await config.onNavigateToPreview({
          policyId,
          pages: currentPages,
          zoom: currentZoom,
          userId: user.id,
          title: proposalTitle,
          footer,
          includeCoverToc,
        });
      } catch {
        // Non-fatal — session storage covers same-tab navigation
      }
    } else if (config?.saveFn && user?.id) {
      try {
        await config.saveFn({
          title: proposalTitle,
          pages: currentPages,
          zoom: currentZoom,
          userId: user.id,
          orgId: orgId ?? undefined,
          footer,
          includeCoverToc,
        });
      } catch {
        // Non-fatal
      }
    }
    const idParam = policyId ? `&id=${policyId}` : "";
    navigate(`${previewRoute}?tab=preview${idParam}`);
  };

  // Approval workflow: when config.approval is set (Revisr + flag on) and the
  // viewer is a member whose policy is not yet approved, the Save button on both
  // the editor and preview pages becomes "Request Approval" and the Export
  // dropdown is hidden. While a request is pending the button is disabled and
  // reads "Pending approval"; a prior rejection surfaces the admin's reason.
  // Other consumers don't set config.approval, so this is a no-op for them.
  const approval = config?.approval;
  // True for the rest of the session after the member clicks Request Approval,
  // so the button flips to "Requested" and is disabled immediately (prevents a
  // second click during the post-request navigation delay).
  const [approvalSubmitted, setApprovalSubmitted] = useState(false);
  const [reviewReason, setReviewReason] = useState("");
  const [isReviewing, setIsReviewing] = useState(false);
  const approvalState = approval?.state ?? "none";
  const gated =
    !!approval && approval.required && !approval.isOrgAdmin && approvalState !== "approved";
  const pendingApproval = (gated && approvalState === "pending") || approvalSubmitted;
  const rejectedReason =
    approvalState === "rejected" ? approval?.rejectedReason ?? null : null;
  // Admin approval review - present only when an org admin opened this preview
  // from the Policy Approvals queue (see BrandPolicyEditorWrapper).
  const review = config?.approvalReview;
  // Lets the member dismiss the rejection note after reading it. Resets when the
  // reason changes, so a fresh rejection re-shows the note.
  const [reasonDismissed, setReasonDismissed] = useState(false);
  useEffect(() => {
    setReasonDismissed(false);
  }, [rejectedReason]);

  // Save button handler:
  // - Gated members (approval workflow, not yet approved): request org admin
  //   approval directly from either the editor or preview page.
  // - Editor page (previewRoute defined): navigate to preview page
  // - On preview page (no previewRoute): save directly and navigate to afterSaveRoute
  const handleSave = () => {
    if (isSaving || pendingApproval) return;
    if (!user?.id) {
      toast({ title: "Not authenticated", variant: "destructive" });
      return;
    }
    const pages = (editorState as any)?.pages ?? editorInitialData?.pages ?? [];
    if (!pages.length) {
      toast({ title: "Nothing to save", description: "The proposal has no content.", variant: "destructive" });
      return;
    }
    // Gated members: request approval instead of saving / navigating to preview.
    if (gated) {
      void handleConfirmSave();
      return;
    }
    // Editor page: navigate to preview instead of opening save dialog
    if (previewRoute) {
      void handleNavigateToPreview();
      return;
    }
    // Preview page: save directly using the document name already loaded from the DB
    void handleConfirmSave();
  };

  // Admin review actions (only wired when config.approvalReview is set, i.e. an
  // org admin opened this preview from the Policy Approvals queue).
  const handleReviewApprove = async () => {
    if (!review || isReviewing) return;
    setIsReviewing(true);
    try {
      await review.onApprove();
      toast({ title: "Policy approved", description: "The member can now save and export this policy." });
      navigate("/revisr/policy-approvals");
    } catch (err) {
      console.error("[BrandedProposalEditor] Approve failed:", err);
      toast({ title: "Approve failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setIsReviewing(false);
    }
  };

  const handleReviewReject = async () => {
    if (!review || isReviewing) return;
    setIsReviewing(true);
    try {
      await review.onReject(reviewReason.trim() || undefined);
      toast({ title: "Policy rejected", description: "The member can edit and re-request approval." });
      setReviewReason("");
      navigate("/revisr/policy-approvals");
    } catch (err) {
      console.error("[BrandedProposalEditor] Reject failed:", err);
      toast({ title: "Reject failed", description: "Please try again.", variant: "destructive" });
    } finally {
      setIsReviewing(false);
    }
  };

  const handleConfirmSave = async () => {
    const title = saveTitle.trim() || proposalTitle;
    const stateAny = editorState as any;
    const pages = stateAny?.pages ?? editorInitialData?.pages ?? [];

    setIsSaving(true);
    try {
      // Gated members request approval instead of saving as completed. The
      // requestApproval callback persists the editor payload + flips the record
      // to 'pending_approval' (see BrandPolicyEditorWrapper).
      if (gated && approval) {
        await approval.requestApproval({
          title,
          pages,
          zoom: stateAny?.zoom ?? 1,
          userId: user!.id,
          orgId: orgId ?? undefined,
          footer,
          includeCoverToc,
        });
        setApprovalSubmitted(true);
        sessionStorage.removeItem(sessionResultKey);
        sessionStorage.removeItem(sessionDataKey);
        setShowSaveDialog(false);
        toast({
          title: "Approval requested",
          description: `"${title}" is awaiting organisation admin approval. You can save and export once it's approved.`,
        });
        setTimeout(() => navigate(afterSaveRoute), 1500);
        return;
      }
      if (config?.saveFn) {
        await config.saveFn({
          title,
          pages,
          zoom: stateAny?.zoom ?? 1,
          userId: user!.id,
          orgId: orgId ?? undefined,
          footer,
          includeCoverToc,
        });
      } else {
        const { supabase, supabasePublic } = await import("../utils/supabaseClient");

        const safeName = title
          .replace(/[^a-zA-Z0-9\-_\s]/g, "")
          .replace(/\s+/g, "_")
          .substring(0, 50);
        const filePath = `${user!.id}/${safeName}_${Date.now()}.json`;
        const content = JSON.stringify({
          version: 2,
          pages,
          zoom: stateAny?.zoom ?? 1,
        });

        const { error: uploadError } = await supabasePublic.storage
          .from("scoping-proposals")
          .upload(filePath, content, { contentType: "application/json", upsert: true });

        if (uploadError) throw uploadError;

        const { data: docData, error: docError } = await supabasePublic
          .from("scopingai_documents")
          .insert({ title, proposalFilePath: filePath, user_id: user!.id, status: "created" })
          .select("id")
          .single();

        if (docError) throw docError;

        const { error: proposalError } = await supabase
          .from("scoping_proposals")
          .insert({
            title,
            user_id: user!.id,
            status: "created",
            proposal_file_path: filePath,
            is_current: true,
            version_no: 1,
            doc_id: docData?.id ?? null,
          });

        if (proposalError) throw proposalError;
      }

      sessionStorage.removeItem(sessionResultKey);
      sessionStorage.removeItem(sessionDataKey);

      setShowSaveDialog(false);
      toast({ title: `${noun} saved!`, description: `"${title}" has been saved.` });
      if (config?.feedback?.enabled && policyId && user?.id) {
        const { policyFeedbackExists } = await import("@/features/revisr/services/policyFeedbackService");
        const already = await policyFeedbackExists(policyId, user.id, orgId ?? undefined);
        if (!already) {
          setFeedbackPolicyId(policyId);
          return; // modal's onClose handles navigation
        }
      }
      setTimeout(() => navigate(afterSaveRoute), 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Save failed";
      console.error("[BrandedProposalEditor] Save failed:", err);
      toast({ title: "Save failed", description: message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerateDocument = async (userContext: string) => {
    if (isRegeneratingDoc) return;

    const livePages =
      (editorState as PlateEditorState | null)?.pages ??
      (Array.isArray(storedPages) ? storedPages : []);

    // ── Cover/TOC-only path: skip the LLM, just manipulate the page list ──
    // Fires when the prompt mentions the cover/TOC pages but asks for no
    // content change. Preserves all content-page edits (we never touch the
    // content pages themselves, only prepend/strip the first two).
    const { coverTocMention, removeIntent, addIntent, contentChange } = parseCoverTocIntent(userContext);
    if (coverTocMention && !contentChange) {
      // Only strip the first two when cover/TOC are actually present — otherwise
      // slice(2) would drop real content pages.
      const contentPages = includeCoverToc ? livePages.slice(2) : livePages;
      if (removeIntent) {
        setStoredPages(contentPages as unknown[]);
        setIncludeCoverToc(false);
        setEditorKey((k) => k + 1);
        setShowDocRegenDialog(false);
        toast({ title: 'Cover & TOC removed', description: 'The cover page and table of contents have been removed.' });
        return;
      }
      if (addIntent) {
        const titles = extractContentPageTitles(contentPages);
        const cover = buildCoverPage(branding as BrandingData, proposalTitle);
        const toc = buildTocPage(titles);
        // Prepend freshly-built cover+TOC to the existing content pages.
        setStoredPages([cover, toc, ...contentPages] as unknown[]);
        setIncludeCoverToc(true);
        setEditorKey((k) => k + 1);
        setShowDocRegenDialog(false);
        toast({ title: 'Cover & TOC added', description: 'The cover page and table of contents have been added.' });
        return;
      }
      // cover/TOC mentioned but no clear add/remove intent → fall through to regen.
    }

    // ── Regen path: full LLM rewrite, then apply the cover/TOC intent ──
    const controller = new AbortController();
    docRegenAbortRef.current = controller;
    setIsRegeneratingDoc(true);
    try {
      // Content-only markdown for AI context — matches save/export (excludes cover + TOC).
      const fromSections = sections
        .map((s) => {
          const title = (s.title ?? '').replace(/^#+\s*/, '').replace(/^\d+[\.\)]\s*/, '').trim();
          return `## ${title}\n\n${s.content ?? ''}`;
        })
        .join('\n\n');
      const currentMarkdown =
        contentPagesToMarkdown(livePages as { plateValue?: unknown }[]) ||
        fromSections.trim();

      const newMarkdown = await regenerateDocument(
        policyId ?? undefined,
        { fullDocumentContext: currentMarkdown, additionalPrompt: userContext || undefined },
        user?.id,
        orgId ?? undefined,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      if (!newMarkdown) throw new Error('No content returned from AI');

      const newSections = parseMarkdownToSections(newMarkdown);
      let rebuiltPages = sectionsToPlatePages(newSections, branding as BrandingData, proposalTitle);
      // Apply cover/TOC intent to the rebuilt page list.
      if (coverTocMention && removeIntent) {
        rebuiltPages = rebuiltPages.slice(2);
        setIncludeCoverToc(false);
      } else {
        setIncludeCoverToc(true);
      }
      setStoredPages(rebuiltPages as unknown[]);
      setSections(newSections);
      setEditorKey((k) => k + 1);
      setShowDocRegenDialog(false);
      toast({ title: 'Document regenerated', description: 'The whole policy has been rewritten.' });
      // Auto-persist the regenerated markdown (content only) so it survives
      // back navigation to the generation page without an explicit save.
      // Non-fatal: the regen already succeeded in the editor.
      if (config?.onDocumentRegenerated) {
        try {
          await config.onDocumentRegenerated({
            policyId: policyId ?? undefined,
            markdown: newMarkdown,
            userId: user?.id,
            orgId: orgId ?? undefined,
            title: proposalTitle,
          });
        } catch (persistErr) {
          console.warn('[BrandedProposalEditor] Auto-save of regenerated content failed:', persistErr);
        }
      }
    } catch (err) {
      if (controller.signal.aborted) return; // intentional cancel/unmount — no error toast
      const message = err instanceof Error ? err.message : 'Regeneration failed';
      toast({ title: 'Regeneration failed', description: message, variant: 'destructive' });
    } finally {
      if (docRegenAbortRef.current === controller) docRegenAbortRef.current = null;
      setIsRegeneratingDoc(false);
    }
  };

  const handleRegenerateSectionRequest = (request: RegenerateSectionRequest) => {
    setRegenRequest(request);
    setShowRegenDialog(true);
  };

  const handleRegenerate = async (composedPrompt: string) => {
    if (!regenRequest || isRegenerating) return;

    setIsRegenerating(true);

    try {
      const regenerated = await regenerateSection(
        policyId ?? 'draft',
        {
          sectionIndex: regenRequest.sectionIndex,
          sectionTitle: regenRequest.sectionTitle,
          additionalPrompt: composedPrompt,
          fullDocumentContext: regenRequest.fullDocumentMarkdown,
        },
        user?.id,
        orgId ?? undefined,
      );

      if (regenerated.action !== 'remove' && !regenerated.content) {
        throw new Error("No content returned from AI");
      }

      // Strip leading heading if present (the editor already has the heading)
      let cleanContent = regenerated.content.trim();
      const headingMatch = cleanContent.match(/^#{1,6}\s+.+\n*/);
      if (headingMatch) {
        cleanContent = cleanContent.slice(headingMatch[0].length).trim();
      }

      const livePages =
        (editorState as PlateEditorState | null)?.pages ??
        (Array.isArray(storedPages) ? storedPages : []);
      const baseSections =
        sections.length > 0
          ? sections
          : parseMarkdownToSections(
              contentPagesToMarkdown(livePages as { plateValue?: unknown }[]),
            );
      const regenTitle = regenRequest.sectionTitle
        .replace(/^#+\s*/, '')
        .replace(/^\d+[\.\)]\s*/, '')
        .trim();
      const matchesRegenTitle = (s: { title?: string }) =>
        (s.title ?? '').replace(/^#+\s*/, '').replace(/^\d+[\.\)]\s*/, '').trim() === regenTitle;
      // The prompt asked to delete this section — drop it; otherwise splice in the new content.
      const updated =
        regenerated.action === 'remove'
          ? baseSections.filter((s) => !matchesRegenTitle(s))
          : baseSections.map((s) => (matchesRegenTitle(s) ? { ...s, content: cleanContent } : s));

      const rebuiltPages = sectionsToPlatePages(updated, branding as BrandingData, proposalTitle);
      setSections(updated);
      setStoredPages(rebuiltPages as unknown[]);
      setEditorKey((k) => k + 1);
      setShowRegenDialog(false);
      if (regenerated.action === 'remove') {
        toast({ title: "Section removed", description: `"${regenRequest.sectionTitle}" has been removed from the document.` });
      } else {
        toast({ title: "Section regenerated", description: `"${regenRequest.sectionTitle}" has been updated.` });
      }

      // Auto-persist (content only), same as whole-document regen — without
      // this, a section regen in the editor only lived in React state/session
      // and was lost on navigation away unless the user explicitly hit Save.
      if (config?.onDocumentRegenerated) {
        try {
          const newMarkdown = contentPagesToMarkdown(rebuiltPages as { plateValue?: unknown }[]);
          await config.onDocumentRegenerated({
            policyId: policyId ?? undefined,
            markdown: newMarkdown,
            userId: user?.id,
            orgId: orgId ?? undefined,
            title: proposalTitle,
          });
        } catch (persistErr) {
          console.warn('[BrandedProposalEditor] Auto-save of regenerated section failed:', persistErr);
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Regeneration failed";
      toast({ title: "Regeneration failed", description: message, variant: "destructive" });
    } finally {
      setIsRegenerating(false);
    }
  };

  // ── Export helpers ────────────────────────────────────────────────────────
  // HTML/PDF serialization lives in @/features/revisr/utils/exportPlate so the
  // PolicyDocuments list view and this editor produce byte-identical output.
  // The shared serializer mirrors nodeRenderers.tsx exactly: h1=primary+border,
  // h2/h3=secondary+border, BoldLeaf=primary+weight 600, list styles, etc.


  const handleExportPDF = async () => {
    setExportMenuOpen(false);
    setIsExporting(true);
    try {
      const state = editorState as PlateEditorState | null;
      const pages: PlatePage[] = state?.pages ?? [];
      if (!pages.length) {
        toast({ title: "Nothing to export", variant: "destructive" });
        return;
      }

      // Use the Puppeteer (server-side HTML→PDF) path so output matches the
      // editor's branded rendering exactly. Falls back to client jsPDF if the
      // VITE_PDF_DOWNLOAD_SERVER env var isn't set.
      await exportPlatePagesToPuppeteerPdf(pages, proposalTitle || 'document', brandColors, footerMeta);
      toast({ title: "PDF exported successfully" });
    } catch (err) {
      console.error('PDF export failed:', err);
      toast({ title: "Export failed", description: err instanceof Error ? err.message : 'Unknown error', variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  };


  const handleExportHtml = async () => {
    setExportMenuOpen(false);
    setIsExporting(true);
    try {
      const state = editorState as PlateEditorState | null;
      const pages: PlatePage[] = state?.pages ?? [];
      if (!pages.length) {
        toast({ title: "Nothing to export", variant: "destructive" });
        return;
      }

      // Shared util builds the self-contained HTML so this editor and the
      // Your Policies list export byte-identical output.
      await exportPlatePagesToHtml(pages, proposalTitle || 'document', brandColors, footerMeta);

      toast({ title: "HTML exported successfully" });
    } catch (err) {
      console.error('HTML export failed:', err);
      toast({ title: "Export failed", description: err instanceof Error ? err.message : 'Unknown error', variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportDocx = async () => {
    setExportMenuOpen(false);
    setIsExporting(true);
    try {
      const state = editorState as PlateEditorState | null;
      const pages: PlatePage[] = state?.pages ?? [];
      if (!pages.length) {
        toast({ title: "Nothing to export", variant: "destructive" });
        return;
      }
      await exportPlatePagesToDocx(pages, proposalTitle || 'document', brandColors, footerMeta);
      toast({ title: "Word document exported successfully" });
    } catch (err) {
      console.error('DOCX export failed:', err);
      toast({ title: "Export failed", description: err instanceof Error ? err.message : 'Unknown error', variant: "destructive" });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <LayoutComponent>
      <div className="flex flex-col h-[calc(100vh-3.5rem)] overflow-hidden">
        {/* Fixed Header - Responsive */}
        <div className="flex flex-wrap items-center justify-between px-2 sm:px-4 py-2 sm:py-3 bg-white border-b border-gray-200 shrink-0 gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {branding?.logo_url && (
              <img
                src={branding.logo_url}
                alt="Organisation logo"
                className="object-contain hidden sm:block"
                style={{ maxHeight: "36px", maxWidth: "100px" }}
              />
            )}
            <Button
              variant="ghost"
              onClick={() => navigate(backRoute)}
              className="gap-1 sm:gap-2 px-2 sm:px-3"
              size="sm"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          </div>

          <h1 className="text-sm sm:text-base font-semibold text-gray-900 truncate max-w-[120px] sm:max-w-xs text-center min-w-0">
            {proposalTitle}
          </h1>

          <div className="flex items-center gap-1 sm:gap-2">
            {/* Regenerate whole document — editor (edit) page only. */}
            {previewRoute && (
              <Button
                variant="outline"
                onClick={() => setShowDocRegenDialog(true)}
                disabled={isRegeneratingDoc}
                className="gap-1 sm:gap-2 px-2 sm:px-3"
                size="sm"
              >
                {isRegeneratingDoc ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Regenerate document</span>
              </Button>
            )}
            {/* On-demand feedback — rate + comment any time (requires a saved policy id). */}
            {!review && (
            <Button
              variant="outline"
              onClick={() => setShowFeedbackModal(true)}
              disabled={!(policyId && user?.id)}
              title={policyId && user?.id ? undefined : "Save the policy first to leave feedback"}
              className="gap-1 sm:gap-2 px-2 sm:px-3"
              size="sm"
            >
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Feedback</span>
            </Button>
            )}
            {/* Approval status - surfaced to gated members when a prior request
                was rejected, so they see the admin's reason before re-requesting. */}
            {gated && rejectedReason && (
              <span
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ background: "rgba(239,68,68,0.12)", color: "#b91c1c" }}
              >
                <ShieldAlert className="h-3.5 w-3.5" />
                Rejected
              </span>
            )}
            {/* Admin approval review - shown only when an org admin opened this
                preview from the Policy Approvals queue (config.approvalReview). */}
            {review && (
              <div className="flex items-center gap-2">
                <Input
                  value={reviewReason}
                  onChange={(e) => setReviewReason(e.target.value)}
                  placeholder="Rejection reason (optional)"
                  disabled={isReviewing}
                  className="h-8 w-44 sm:w-56 text-xs"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReviewReject}
                  disabled={isReviewing}
                  className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50 px-2 sm:px-3"
                >
                  {isReviewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                  <span className="hidden sm:inline">Reject</span>
                </Button>
                <Button
                  size="sm"
                  onClick={handleReviewApprove}
                  disabled={isReviewing}
                  className="gap-1.5 bg-[#33b864] hover:bg-[#2da358] text-white px-2 sm:px-3"
                >
                  {isReviewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  <span className="hidden sm:inline">Accept</span>
                </Button>
              </div>
            )}
            {/* Save button.
                - Gated members: "Request Approval" (or "Pending approval" while
                  waiting). Editor + preview pages both act directly.
                - Editor page (previewRoute set): "Save & Preview" -> preview page.
                - Preview page (no previewRoute): "Save". */}
            {!review && (
            <Button
              variant={previewRoute ? "default" : "outline"}
              onClick={handleSave}
              disabled={isSaving || pendingApproval}
              className={
                previewRoute
                  ? "bg-[#33b864] hover:bg-[#2da358] text-white gap-1 sm:gap-2 px-2 sm:px-3 text-xs sm:text-sm"
                  : "gap-1 sm:gap-2 px-2 sm:px-3"
              }
              size="sm"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : pendingApproval ? (
                <Clock className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">
                {isSaving
                  ? "Saving..."
                  : pendingApproval
                    ? "Requested"
                    : gated
                      ? "Request Approval"
                      : previewRoute
                        ? "Save & Preview"
                        : "Save"}
              </span>
              {previewRoute && !gated && !isSaving && <ArrowRight className="h-4 w-4" />}
            </Button>
            )}

            {/* Export dropdown — only on the preview page, not while editing.
                Hidden for gated members (policy not yet approved) and in admin
                review mode. */}
            {!previewRoute && !gated && !review && (
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setExportMenuOpen((o) => !o)}
                disabled={isExporting}
                className="gap-1 sm:gap-2 px-2 sm:px-3"
                size="sm"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Export</span>
              </Button>
              {exportMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setExportMenuOpen(false)} />
                  <div className="absolute right-0 top-9 z-50 bg-white border border-gray-200 rounded shadow-lg py-1 w-48">
                    <button
                      type="button"
                      onClick={handleExportPDF}
                      disabled={isExporting}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileText className="h-3.5 w-3.5 text-red-500" />
                      Export as PDF
                    </button>
                    <button
                      type="button"
                      onClick={handleExportDocx}
                      disabled={isExporting}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileText className="h-3.5 w-3.5 text-blue-500" />
                      Export as Word (.docx)
                    </button>
                    <button
                      type="button"
                      onClick={handleExportHtml}
                      disabled={isExporting}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileText className="h-3.5 w-3.5 text-orange-500" />
                      Export as HTML
                    </button>
                  </div>
                </>
              )}
            </div>
            )}
          </div>
        </div>

        {/* Rejection notice - shown to a gated member whose policy was rejected,
            so the admin's reason is visible in a readable block (not crammed into
            the status pill). Visible on both the editor and preview pages. */}
        {/* Rejection note - a neutral note (not an error) so the member can read
            the admin's reason calmly. Visible on both the editor and preview pages. */}
        {gated && rejectedReason && !reasonDismissed && (
          <div className="mx-3 sm:mx-4 mt-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 shrink-0 relative">
            <button
              type="button"
              onClick={() => setReasonDismissed(true)}
              className="absolute top-2 right-2 p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors"
              aria-label="Dismiss note"
              title="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <div className="flex items-start gap-2 pr-6">
              <Info className="h-4 w-4 mt-0.5 shrink-0 text-slate-500" />
              <div className="min-w-0">
                <p className="font-medium text-slate-800">This policy was rejected by an organisation admin.</p>
                <p className="mt-1 break-words">
                  <span className="font-medium">Reason:</span> {rejectedReason}
                </p>
                <p className="mt-1 text-slate-500">Edit the policy and request approval again.</p>
              </div>
            </div>
          </div>
        )}

        {/* Main content area */}
        <div className="flex flex-1 overflow-hidden">
          {/* Editor — branded via CSS variables consumed by node renderers (h1/h2/h3, p, blockquote).
              Variables fall back to gray defaults when branding is absent, so unbranded callers
              of ProposalPagesEditor are unaffected. */}
          <div className="flex flex-1 flex-col overflow-hidden">
          <div
            className="flex-1 overflow-hidden bg-white branded-policy-editor"
            style={{
              fontFamily: toCssFontFamily(brandColors.fontFamily),
              // Policy fonts are forced to black — branding colours are intentionally ignored.
              ['--brand-primary' as const]:        '#000000',
              ['--brand-secondary' as const]:      '#000000',
              ['--brand-font-color' as const]:     '#000000',
              ['--brand-primary-subtle' as const]: '#00000018',
            } as React.CSSProperties}
          >
            {isLoaded ? (
              <ProposalPagesEditor
                key={editorKey}
                config={editorConfig}
                initialData={editorInitialData}
                onChange={(state) => setEditorState(state)}
                onRegenerateSection={handleRegenerateSectionRequest}
                isRegenerating={isRegenerating}
                className="h-full w-full"
                footerMeta={footerMeta}
                onFooterChange={(updates) => setFooter((f) => ({ ...f, ...updates }))}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-[#33b864]" />
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Save Proposal Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={(open) => !isSaving && setShowSaveDialog(open)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Save {noun}</DialogTitle>
            </DialogHeader>

            <div className="space-y-2 py-2">
              <label className="text-sm font-medium text-gray-700">{noun} Name</label>
              <Input
                value={saveTitle}
                onChange={(e) => setSaveTitle(e.target.value)}
                placeholder={`Enter ${noun.toLowerCase()} name...`}
                disabled={isSaving}
                onKeyDown={(e) => e.key === "Enter" && !isSaving && saveTitle.trim() && handleConfirmSave()}
                autoFocus
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowSaveDialog(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSave}
                disabled={!saveTitle.trim() || isSaving}
                className={`bg-${accent}-500 hover:bg-${accent}-600 text-white gap-2`}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Regenerate Section Dialog */}
        <RegenerationDialog
          open={showRegenDialog}
          onOpenChange={setShowRegenDialog}
          sectionTitle={regenRequest?.sectionTitle ?? ''}
          onRegenerate={handleRegenerate}
          isRegenerating={isRegenerating}
        />

        {/* Regenerate Document Dialog */}
        <DocumentRegenerateDialog
          open={showDocRegenDialog}
          onOpenChange={setShowDocRegenDialog}
          onRegenerate={handleRegenerateDocument}
          isRegenerating={isRegeneratingDoc}
        />

        {feedbackPolicyId && user?.id && (
          <PolicyFeedbackModal
            open
            policyId={feedbackPolicyId}
            userId={user.id}
            orgId={orgId ?? undefined}
            onClose={() => { setFeedbackPolicyId(null); navigate(afterSaveRoute); }}
          />
        )}

        {/* On-demand feedback modal — closes only, does NOT navigate. */}
        {showFeedbackModal && policyId && user?.id && (
          <PolicyFeedbackModal
            open
            policyId={policyId}
            userId={user.id}
            orgId={orgId ?? undefined}
            onClose={() => setShowFeedbackModal(false)}
          />
        )}
      </div>
    </LayoutComponent>
  );
};

export default BrandedProposalEditor;
